import './applicantSubmitPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import { ThemeProvider } from '@mui/material/styles';
import { isValidDate } from '@thesavingsgroup/date-utils/isValidDate';
import {
  DATE_MM_DD_YYYY_PATTERN,
  SSN_DASHED_PATTERN,
} from '@thesavingsgroup/regex';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode, useEffect, useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { CheckedIcon } from '../../../components/Icons/CheckedIcon';
import { UncheckedIcon } from '../../../components/Icons/UncheckedIcon';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useLeadChannelStore from '../../../store/leadChannelStore/useLeadChannelStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const {
    addCoapplicant,
    dateOfBirth,
    isSubmittingApplication,
    clearSubmitApplicationFailure,
    setDateOfBirth,
    submitApplication,
    submitApplicationFailedMessage,
  } = useApplicantStore();

  const { coapplicantDateOfBirth, setCoapplicantDateOfBirth } =
    useCoapplicantStore();

  const { lastFetchedLeadChannelCode } = useLeadChannelStore();

  const isPrequalDisclosureEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_PREQUAL_DISCLOSURE_CP,
    { leadChannelCode: lastFetchedLeadChannelCode },
  );

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      dateOfBirthValue: dateOfBirth || '',
      ssnValue: '',
      coapplicantDateOfBirthValue: coapplicantDateOfBirth || '',
      coapplicantSsnValue: '',
      prequalDisclosureValue: false,
      termsValue: false,
    },
    delayError: 200,
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const shouldIncludeCoapplicant = useMemo(
    (): boolean => addCoapplicant === YES,
    [addCoapplicant],
  );

  const dateOfBirthValidation = {
    required: t('validation.dateOfBirth.required'),
    pattern: {
      value: DATE_MM_DD_YYYY_PATTERN,
      message: t('validation.dateOfBirth.invalid'),
    },
    validate: (value: string): string | boolean => {
      // Check if the date of birth is a valid date
      if (!isValidDate(value)) {
        return t('validation.dateOfBirth.invalid');
      }

      const dateOfBirthParsed = Date.parse(value);

      // Check if the date of birth is a future date
      const currentDate = new Date();
      if (new Date(dateOfBirthParsed) > currentDate) {
        return t('validation.dateOfBirth.futureDate');
      }

      const hundredYearsAgo = new Date();
      hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);

      // Check if the date of birth is 100 years ago or more
      if (new Date(dateOfBirthParsed) <= hundredYearsAgo) {
        return t('validation.dateOfBirth.invalid');
      }

      return true;
    },
  };

  const ssnValidation = {
    pattern: {
      value: SSN_DASHED_PATTERN,
      message: t('validation.ssn.invalid'),
    },
  };

  const termsValidation = {
    required: t('validation.terms.required'),
  };

  const prequalDisclosureValidation = {
    required: t('validation.prequalDisclosure.required'),
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      dateOfBirthValue,
      ssnValue,
      coapplicantDateOfBirthValue,
      coapplicantSsnValue,
      prequalDisclosureValue,
    } = values;

    if (!dateOfBirthValue) {
      return;
    }

    if (shouldIncludeCoapplicant && !coapplicantDateOfBirthValue) {
      return;
    }

    setDateOfBirth(dateOfBirthValue);

    if (shouldIncludeCoapplicant) {
      setCoapplicantDateOfBirth(coapplicantDateOfBirthValue);
    }

    const successfullySubmitted = await submitApplication(
      ssnValue || undefined,
      coapplicantSsnValue || undefined,
      isPrequalDisclosureEnabled && prequalDisclosureValue,
    );

    if (successfullySubmitted) {
      navigate(next());
    }
  };

  const checkboxClicked = (): void => {
    // `shouldValidate` clears the error the moment it is ticked. A programmatic
    // toggle fires neither the change nor the blur the mode would validate on.
    setValue('termsValue', !getValues('termsValue'), { shouldValidate: true });
  };

  const prequalDisclosureClicked = (): void => {
    setValue('prequalDisclosureValue', !getValues('prequalDisclosureValue'), {
      shouldValidate: true,
    });
  };

  const checkboxClassName = useMemo(
    (): string =>
      [
        'checkbox-described',
        'checkbox-described-sm',
        watch('termsValue') && 'checkbox-described-checked',
        !isEmpty(errors.termsValue) && 'checkbox-described-error',
      ]
        .filter(Boolean)
        .join(' '),
    [watch('termsValue'), errors.termsValue],
  );

  const prequalDisclosureClassName = useMemo(
    (): string =>
      [
        'checkbox-described',
        'checkbox-described-sm',
        watch('prequalDisclosureValue') && 'checkbox-described-checked',
        !isEmpty(errors.prequalDisclosureValue) && 'checkbox-described-error',
      ]
        .filter(Boolean)
        .join(' '),
    [watch('prequalDisclosureValue'), errors.prequalDisclosureValue],
  );

  // on unmount, clear the submit application failure message
  useEffect(
    (): (() => void) => (): void => {
      clearSubmitApplicationFailure();
    },
    [],
  );

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-submit-page" sx={theme.pageLayout}>
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantSubmit.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantSubmit.form.h1}>
                {t('ApplicantSubmitPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantSubmit.form.h2}>
                {t('ApplicantSubmitPage.subtitle')}
              </h2>

              {!isEmpty(submitApplicationFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.applicantSubmit.alert.error}
                    icon={false}
                    severity="error"
                  >
                    {submitApplicationFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <NumberInput
                    {...register('dateOfBirthValue', dateOfBirthValidation)}
                    data-testid={
                      locator.pages.applicantSubmit.form.dateOfBirthInput
                    }
                    error={errors.dateOfBirthValue}
                    errorTestId={
                      locator.pages.applicantSubmit.form.dateOfBirthInputError
                    }
                    label={t('ApplicantSubmitPage.dateOfBirth')}
                    dob
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register('ssnValue', ssnValidation)}
                    data-testid={locator.pages.applicantSubmit.form.ssnInput}
                    error={errors.ssnValue}
                    errorTestId={
                      locator.pages.applicantSubmit.form.ssnInputError
                    }
                    label={t('ApplicantSubmitPage.socialSecurityNumber')}
                    ssn
                  />
                </div>
              </div>

              {shouldIncludeCoapplicant && (
                <div
                  data-testid={
                    locator.pages.applicantSubmit.form.coapplicantSectionRoot
                  }
                  style={{ marginBottom: '3rem' }}
                >
                  <h3
                    data-testid={
                      locator.pages.applicantSubmit.form.coapplicantSectionH3
                    }
                  >
                    {t('ApplicantSubmitPage.coapplicant')}
                  </h3>

                  <div>
                    <NumberInput
                      {...register(
                        'coapplicantDateOfBirthValue',
                        dateOfBirthValidation,
                      )}
                      data-testid={
                        locator.pages.applicantSubmit.form
                          .coapplicantDateOfBirthInput
                      }
                      error={errors.coapplicantDateOfBirthValue}
                      errorTestId={
                        locator.pages.applicantSubmit.form
                          .coapplicantDateOfBirthInputError
                      }
                      label={t('ApplicantSubmitPage.dateOfBirth')}
                      dob
                    />
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <NumberInput
                      {...register('coapplicantSsnValue', ssnValidation)}
                      data-testid={
                        locator.pages.applicantSubmit.form.coapplicantSsnInput
                      }
                      error={errors.coapplicantSsnValue}
                      errorTestId={
                        locator.pages.applicantSubmit.form
                          .coapplicantSsnInputError
                      }
                      label={t('ApplicantSubmitPage.socialSecurityNumber')}
                      ssn
                    />
                  </div>
                </div>
              )}

              <div style={{ marginTop: '3rem' }}>
                <h3
                  className="agreements-heading"
                  data-testid={
                    locator.pages.applicantSubmit.form.agreementsHeading
                  }
                >
                  {shouldIncludeCoapplicant
                    ? t('ApplicantSubmitPage.agreeToFollowingCoapplicant')
                    : t('ApplicantSubmitPage.agreeToFollowing')}
                </h3>

                <CssBaseline>
                  <Container
                    className={checkboxClassName}
                    disableGutters
                    onClick={checkboxClicked}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...register('termsValue', termsValidation)}
                          checked={watch('termsValue', false)}
                          checkedIcon={<CheckedIcon />}
                          data-testid={
                            locator.pages.applicantSubmit.form.termsCheckbox
                          }
                          icon={<UncheckedIcon />}
                        />
                      }
                      label={
                        <Trans
                          components={{
                            terms: (
                              <a
                                data-testid={
                                  locator.pages.applicantSubmit.form.termsLink
                                }
                                href="https://www.thesavingsgroup.com/terms-of-use/"
                                rel="noreferrer"
                                target="_blank"
                                onClick={(event): void => {
                                  event.stopPropagation();
                                }}
                              />
                            ),
                            privacy: (
                              <a
                                data-testid={
                                  locator.pages.applicantSubmit.form.privacyLink
                                }
                                href="https://www.thesavingsgroup.com/privacy-policy/"
                                rel="noreferrer"
                                target="_blank"
                                onClick={(event): void => {
                                  event.stopPropagation();
                                }}
                              />
                            ),
                          }}
                          i18nKey={'ApplicantSubmitPage.acknowledgement'}
                        />
                      }
                      // Allows the higher level container to handle the click event
                      style={{ pointerEvents: 'none' }}
                    />
                  </Container>
                </CssBaseline>

                {!isEmpty(errors.termsValue) && (
                  <span
                    className="field-error"
                    data-testid={
                      locator.pages.applicantSubmit.form.termsCheckboxError
                    }
                  >
                    {toString(errors.termsValue.message)}
                  </span>
                )}

                {isPrequalDisclosureEnabled && (
                  <div style={{ marginTop: '1rem' }}>
                    <CssBaseline>
                      <Container
                        className={prequalDisclosureClassName}
                        disableGutters
                        onClick={prequalDisclosureClicked}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...register(
                                'prequalDisclosureValue',
                                prequalDisclosureValidation,
                              )}
                              checked={watch('prequalDisclosureValue', false)}
                              checkedIcon={<CheckedIcon />}
                              data-testid={
                                locator.pages.applicantSubmit.form
                                  .prequalDisclosureCheckbox
                              }
                              icon={<UncheckedIcon />}
                            />
                          }
                          label={t('ApplicantSubmitPage.prequalDisclosure')}
                          // Allows the higher level container to handle the click event
                          style={{ pointerEvents: 'none' }}
                        />
                      </Container>
                    </CssBaseline>

                    {!isEmpty(errors.prequalDisclosureValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.applicantSubmit.form
                            .prequalDisclosureCheckboxError
                        }
                      >
                        {toString(errors.prequalDisclosureValue.message)}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginTop: '3rem' }}>
                <Button
                  data-testid={locator.pages.applicantSubmit.form.submitButton}
                  disabled={!isValid}
                  loader={isSubmittingApplication}
                  fullWidth
                  submit
                >
                  {t('ApplicantSubmitPage.submit')}
                </Button>
              </div>
            </div>
            <footer data-testid={locator.pages.applicantSubmit.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantSubmitPage';
